import type {
  AccessSetupResponse,
  AccessStatusResponse,
  AppSettingsResponse,
  BillingCheckoutsResponse,
  SubscriptionStatusResponse,
  CreateStripeCheckoutRequest,
  CreateStripeCheckoutResponse,
  StripePortalResponse,
  DatabaseBackupCatalogResponse,
  CreateDatabaseBackupResponse,
  AccessLoginRequest,
  AccessLoginResponse,
  AdminQrCard,
  AdminQrCardsResponse,
  AccessUserListResponse,
  AccessUserResponse,
  AdminOverviewResponse,
  ClearLoginJournalResponse,
  CreateOrderRequest,
  CreateAccessUserRequest,
  CreateOrderResponse,
  CreateVenuePhotoRequest,
  CreateMenuItemRequest,
  DeleteAccessUserRequest,
  InitialAccessSetupRequest,
  MenuItemResponse,
  MenuListResponse,
  LoginJournalResponse,
  LocalNetworkInfoResponse,
  OrderListResponse,
  CreateTableRequest,
  DeleteTableRequest,
  TableAccessResponse,
  WaiterCallListResponse,
  WaiterCallResponse,
  TableResponse,
  TableListResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  UpdateAccessSetupRequest,
  UpdateAppSettingsRequest,
  UpdateAccessUserRequest,
  UpdateMenuItemRequest,
  UpdateTableRequest,
  RecoverPasswordRequest,
  RecoverPasswordResponse,
  RestoreDatabaseBackupRequest,
  RestoreDatabaseBackupResponse,
  UpdateDatabaseBackupSettingsRequest,
  UpdateDatabaseBackupSettingsResponse,
  SalesStatisticsResponse,
  SupportRequestPayload,
  SupportRequestResponse,
  CreatePortalReviewRequest,
  CreatePortalReviewResponse,
  CreatePortalReportRequest,
  CreatePortalReportResponse,
  PortalReview,
  PortalAdminAuthRequest,
  PortalAdminAuthResponse,
  PortalAdminBillingResponse,
  PortalReportsResponse,
  PortalVenueListResponse,
  PortalVenueProfileResponse,
  UpdatePortalReportRequest,
  UpdatePortalReportResponse,
  UpdatePortalVenueEnabledRequest,
  UpdatePortalVenueEnabledResponse,
  UpdatePortalVenueLocationRequest,
  UpdatePortalVenueLocationResponse,
  VenuePhotoListResponse,
  VenuePhotoResponse,
  VerifyTableAccessRequest,
} from '../shared/api-contract';
import { ApiEndpoints } from '../shared/api-contract';
import { API_BASE_URL, resolveApiUrl, resolvePortalUrl } from '../config/runtime';

export type { AdminQrCard };

const DESKTOP_SESSION_STORAGE_KEY = 'cafe-desktop-session';

function getDesktopSessionUserId() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(DESKTOP_SESSION_STORAGE_KEY) || 'null') as { user?: { id?: string } } | null;
    return parsed?.user?.id || '';
  } catch {
    return '';
  }
}

function buildJsonHeaders(body?: unknown) {
  const headers: Record<string, string> = body ? { 'Content-Type': 'application/json' } : {};
  const userId = getDesktopSessionUserId();

  if (userId) {
    headers['X-Cafe-User-Id'] = userId;
  }

  return Object.keys(headers).length > 0 ? headers : undefined;
}

// Ошибка запроса с сохранением HTTP-статуса и тела ответа сервера
// (нужно, например, чтобы поймать 402 «нужна подписка» и показать баннер).
export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(endpoint: { path: string; method: string }, body?: unknown): Promise<T> {
  const response = await fetch(resolveApiUrl(endpoint.path), {
    method: endpoint.method,
    headers: buildJsonHeaders(body),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = (payload && typeof payload === 'object' && 'message' in payload && (payload as { message?: string }).message)
      || (payload && typeof payload === 'object' && 'error' in payload && (payload as { error?: string }).error)
      || response.statusText;
    throw new ApiError(String(message), response.status, payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
}

async function portalRequest<T>(endpoint: { path: string; method: string }, body?: unknown): Promise<T> {
  const response = await fetch(resolvePortalUrl(endpoint.path), {
    method: endpoint.method,
    headers: buildJsonHeaders(body),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.error || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
}

export const getMenu = (): Promise<MenuListResponse> => request<MenuListResponse>(ApiEndpoints.menu.list);
export const createMenuItem = (payload: CreateMenuItemRequest): Promise<MenuItemResponse> =>
  request<MenuItemResponse>(ApiEndpoints.menu.createItem, payload);
export const updateMenuItem = (itemId: string, payload: UpdateMenuItemRequest): Promise<MenuItemResponse> =>
  request<MenuItemResponse>(ApiEndpoints.menu.updateItem(itemId), payload);
export const deleteMenuItem = (itemId: string): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.menu.deleteItem(itemId));
export const getOrders = (): Promise<OrderListResponse> => request<OrderListResponse>(ApiEndpoints.orders.list);
export const createOrder = (payload: CreateOrderRequest): Promise<CreateOrderResponse> =>
  request<CreateOrderResponse>(ApiEndpoints.orders.create, payload);
export const updateOrderStatus = (
  orderId: string,
  payload: UpdateOrderStatusRequest,
): Promise<UpdateOrderStatusResponse> =>
  request<UpdateOrderStatusResponse>(ApiEndpoints.orders.updateStatus(orderId), payload);
export const getAdminOverview = (): Promise<AdminOverviewResponse> => request<AdminOverviewResponse>(ApiEndpoints.admin.overview);
export const getAdminSalesStatistics = (date: string): Promise<SalesStatisticsResponse> =>
  request<SalesStatisticsResponse>({ method: 'GET', path: `${ApiEndpoints.admin.statistics.path}?date=${encodeURIComponent(date)}` });
export const getLoginJournal = (): Promise<LoginJournalResponse> => request<LoginJournalResponse>(ApiEndpoints.admin.loginJournal);
export const clearLoginJournal = (): Promise<ClearLoginJournalResponse> => request<ClearLoginJournalResponse>(ApiEndpoints.admin.clearLoginJournal);
export const getLocalNetworkInfo = (): Promise<LocalNetworkInfoResponse> => request<LocalNetworkInfoResponse>(ApiEndpoints.admin.localNetwork);
export const getDatabaseBackups = (): Promise<DatabaseBackupCatalogResponse> => request<DatabaseBackupCatalogResponse>(ApiEndpoints.admin.backups);
export const createDatabaseBackup = (): Promise<CreateDatabaseBackupResponse> => request<CreateDatabaseBackupResponse>(ApiEndpoints.admin.createBackup, {});
export const restoreDatabaseBackup = (payload: RestoreDatabaseBackupRequest): Promise<RestoreDatabaseBackupResponse> =>
  request<RestoreDatabaseBackupResponse>(ApiEndpoints.admin.restoreBackup, payload);
export const updateDatabaseBackupSettings = (payload: UpdateDatabaseBackupSettingsRequest): Promise<UpdateDatabaseBackupSettingsResponse> =>
  request<UpdateDatabaseBackupSettingsResponse>(ApiEndpoints.admin.updateBackupSettings, payload);
export const submitSupportRequest = (payload: SupportRequestPayload): Promise<SupportRequestResponse> =>
  request<SupportRequestResponse>(ApiEndpoints.support.submit, payload);
export const getSubscriptionStatus = (): Promise<SubscriptionStatusResponse> =>
  request<SubscriptionStatusResponse>(ApiEndpoints.billing.subscription);
export const getBillingCheckouts = (): Promise<BillingCheckoutsResponse> =>
  request<BillingCheckoutsResponse>(ApiEndpoints.billing.checkouts);
export const createStripeCheckout = (
  payload: CreateStripeCheckoutRequest,
): Promise<CreateStripeCheckoutResponse> =>
  request<CreateStripeCheckoutResponse>(ApiEndpoints.billing.createStripeCheckout, payload);
export const openStripePortal = (): Promise<StripePortalResponse> =>
  request<StripePortalResponse>(ApiEndpoints.billing.stripePortal, {});
export const getPortalVenueProfile = (): Promise<PortalVenueProfileResponse> =>
  request<PortalVenueProfileResponse>(ApiEndpoints.portal.venueProfile);
export const getPortalVenues = (): Promise<PortalVenueListResponse> =>
  request<PortalVenueListResponse>(ApiEndpoints.portal.venues);
export const getPublicPortalVenues = (): Promise<PortalVenueListResponse> =>
  portalRequest<PortalVenueListResponse>(ApiEndpoints.portal.venues);

const isDesktopRuntime = () => typeof window !== 'undefined' && Boolean(window.__CAFE_DESKTOP_RUNTIME__);

const requestLocalDesktopFallback = async <T>(endpoint: { path: string; method: string }, body?: unknown): Promise<T> => {
  const normalizedPath = endpoint.path.startsWith('/') ? endpoint.path : `/${endpoint.path}`;
  const response = await fetch(`http://127.0.0.1:4000${normalizedPath}`, {
    method: endpoint.method,
    headers: buildJsonHeaders(body),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = (payload && typeof payload === 'object' && 'message' in payload && (payload as { message?: string }).message)
      || (payload && typeof payload === 'object' && 'error' in payload && (payload as { error?: string }).error)
      || response.statusText;
    throw new ApiError(String(message), response.status, payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
};

export const updatePortalVenueLocation = async (payload: UpdatePortalVenueLocationRequest): Promise<UpdatePortalVenueLocationResponse> => {
  try {
    return await request<UpdatePortalVenueLocationResponse>(ApiEndpoints.portal.updateVenueLocation, payload);
  } catch (error) {
    const failedToReachApi = error instanceof TypeError && String(error.message || '').includes('fetch');
    const alreadyLocalApi = /^https?:\/\/(localhost|127\.0\.0\.1):4000$/i.test(API_BASE_URL);
    if (!isDesktopRuntime() || !failedToReachApi || alreadyLocalApi) {
      throw error;
    }

    return requestLocalDesktopFallback<UpdatePortalVenueLocationResponse>(ApiEndpoints.portal.updateVenueLocation, payload);
  }
};
export const createPortalReview = (payload: CreatePortalReviewRequest): Promise<CreatePortalReviewResponse> =>
  portalRequest<CreatePortalReviewResponse>(ApiEndpoints.portal.createReview, payload);
export const createPortalReport = (payload: CreatePortalReportRequest): Promise<CreatePortalReportResponse> =>
  portalRequest<CreatePortalReportResponse>(ApiEndpoints.portal.createReport, payload);
export const getVenueReviews = (venueId: string): Promise<{ reviews: PortalReview[] }> =>
  portalRequest<{ reviews: PortalReview[] }>(ApiEndpoints.portal.venueReviews(venueId));
export const authenticatePortalOwner = (payload: PortalAdminAuthRequest): Promise<PortalAdminAuthResponse> =>
  request<PortalAdminAuthResponse>(ApiEndpoints.portal.adminAuth, payload);
export const getPortalAdminVenues = (ownerEmail: string): Promise<PortalVenueListResponse> =>
  request<PortalVenueListResponse>({ method: 'GET', path: `${ApiEndpoints.portal.adminVenues.path}?ownerEmail=${encodeURIComponent(ownerEmail)}` });
export const getPortalAdminBilling = (ownerEmail: string): Promise<PortalAdminBillingResponse> =>
  request<PortalAdminBillingResponse>({ method: 'GET', path: `${ApiEndpoints.portal.adminBilling.path}?ownerEmail=${encodeURIComponent(ownerEmail)}` });
export const getPortalAdminReports = (ownerEmail: string): Promise<PortalReportsResponse> =>
  request<PortalReportsResponse>({ method: 'GET', path: `${ApiEndpoints.portal.adminReports.path}?ownerEmail=${encodeURIComponent(ownerEmail)}` });
export const updatePortalAdminVenue = (
  venueId: string,
  payload: UpdatePortalVenueEnabledRequest & { ownerEmail: string },
): Promise<UpdatePortalVenueEnabledResponse> =>
  request<UpdatePortalVenueEnabledResponse>(ApiEndpoints.portal.updateAdminVenue(venueId), payload);
export const updatePortalAdminReport = (
  reportId: string,
  payload: UpdatePortalReportRequest & { ownerEmail: string },
): Promise<UpdatePortalReportResponse> =>
  request<UpdatePortalReportResponse>(ApiEndpoints.portal.updateAdminReport(reportId), payload);
export const getVenuePhotos = (): Promise<VenuePhotoListResponse> => request<VenuePhotoListResponse>(ApiEndpoints.venue.photos);
export const createVenuePhoto = (payload: CreateVenuePhotoRequest): Promise<VenuePhotoResponse> =>
  request<VenuePhotoResponse>(ApiEndpoints.venue.createPhoto, payload);
export const deleteVenuePhoto = (photoId: string): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.venue.deletePhoto(photoId));
export const getTables = (): Promise<TableListResponse> => request<TableListResponse>(ApiEndpoints.tables.list);
export const getWaiterCalls = (): Promise<WaiterCallListResponse> =>
  request<WaiterCallListResponse>(ApiEndpoints.tables.waiterCalls);
export const createTable = (payload: CreateTableRequest): Promise<TableResponse> =>
  request<TableResponse>(ApiEndpoints.tables.create, payload);
export const updateTable = (tableId: string, payload: UpdateTableRequest): Promise<TableResponse> =>
  request<TableResponse>(ApiEndpoints.tables.update(tableId), payload);
export const deleteTable = (tableId: string, payload?: DeleteTableRequest): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.tables.delete(tableId), payload);
export const getTableAccess = (accessCode: string): Promise<TableAccessResponse> =>
  request<TableAccessResponse>(ApiEndpoints.tables.access(accessCode));
export const verifyTableAccess = (accessCode: string, payload: VerifyTableAccessRequest): Promise<TableAccessResponse> =>
  request<TableAccessResponse>(ApiEndpoints.tables.verifyAccess(accessCode), payload);
export const callWaiter = (tableId: string): Promise<WaiterCallResponse> =>
  request<WaiterCallResponse>(ApiEndpoints.tables.callWaiter(tableId), {});
export const resolveWaiterCall = (tableId: string): Promise<WaiterCallResponse> =>
  request<WaiterCallResponse>(ApiEndpoints.tables.resolveWaiterCall(tableId), {});
export const loginWithUsername = (payload: AccessLoginRequest): Promise<AccessLoginResponse> =>
  request<AccessLoginResponse>(ApiEndpoints.access.login, payload);
export const loginWithQrToken = (payload: AccessLoginRequest): Promise<AccessLoginResponse> =>
  request<AccessLoginResponse>(ApiEndpoints.access.loginWithQr, payload);
export const getAccessUsers = (): Promise<AccessUserListResponse> =>
  request<AccessUserListResponse>(ApiEndpoints.access.users);
export const createAccessUser = (payload: CreateAccessUserRequest): Promise<AccessUserResponse> =>
  request<AccessUserResponse>(ApiEndpoints.access.createUser, payload);
export const updateAccessUser = (userId: string, payload: UpdateAccessUserRequest): Promise<AccessUserResponse> =>
  request<AccessUserResponse>(ApiEndpoints.access.updateUser(userId), payload);
export const deleteAccessUser = (userId: string, payload: DeleteAccessUserRequest): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.access.deleteUser(userId), payload);
export const getAccessSetup = (): Promise<AccessSetupResponse> =>
  request<AccessSetupResponse>(ApiEndpoints.access.getSetup);
export const getAccessStatus = (): Promise<AccessStatusResponse> =>
  request<AccessStatusResponse>(ApiEndpoints.access.status);
export const getAdminQrCards = (): Promise<AdminQrCardsResponse> =>
  request<AdminQrCardsResponse>(ApiEndpoints.access.adminQrCards);
export const updateAccessSetup = (payload: UpdateAccessSetupRequest): Promise<AccessSetupResponse> =>
  request<AccessSetupResponse>(ApiEndpoints.access.updateSetup, payload);
export const getAppSettings = (): Promise<AppSettingsResponse> =>
  request<AppSettingsResponse>(ApiEndpoints.appSettings.get);
export const updateAppSettings = (payload: UpdateAppSettingsRequest): Promise<AppSettingsResponse> =>
  request<AppSettingsResponse>(ApiEndpoints.appSettings.update, payload);
export const createInitialAccessSetup = (payload: InitialAccessSetupRequest): Promise<AccessLoginResponse> =>
  request<AccessLoginResponse>(ApiEndpoints.access.initialSetup, payload);
export const recoverPassword = (payload: RecoverPasswordRequest): Promise<RecoverPasswordResponse> =>
  request<RecoverPasswordResponse>(ApiEndpoints.access.recoverPassword, payload);
export const recordLogout = (): Promise<{ success: boolean }> =>
  request<{ success: boolean }>({ method: 'POST', path: '/api/access/logout' }, { deviceName: getDeviceNameForAudit() });

function getDeviceNameForAudit() {
  if (typeof navigator === 'undefined') {
    return 'Desktop app';
  }

  return `${navigator.platform || 'Desktop'} • ${navigator.userAgent}`;
}

export const uploadImage = async (file: File): Promise<{ imagePath: string }> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(resolveApiUrl('/api/uploads/images'), {
    method: 'POST',
    headers: buildJsonHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.error || response.statusText);
  }

  return response.json();
};
